import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  createLibraryModule,
  CreateLibraryModuleRequest,
} from "@/modules/learningCenter/features/ModuleLibrary/api/createLibraryModule";
import {
  updateLibraryModule,
  UpdateLibraryModuleRequest,
} from "@/modules/learningCenter/features/ModuleLibrary/api/updateLibraryModule";
import { getLibraryModule } from "@/modules/learningCenter/features/ModuleLibrary/api/getLibraryModule";
import {
  createLibraryModuleFromCourseModule,
  CreateLibraryModuleFromCourseModuleRequest,
} from "@/modules/learningCenter/features/ModuleLibrary/api/createLibraryModuleFromCourseModule";
import { LibraryModule } from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

interface CreateUpdateLibraryModuleState {
  loadingCreate: boolean;
  createdModule: LibraryModule | null;

  loadingGet: boolean;
  getError: string | null;
  fetchedModule: LibraryModule | null;

  loadingUpdate: boolean;
  updatedModule: LibraryModule | null;

  loadingSaveToLibrary: boolean;
  savedToLibraryModule: LibraryModule | null;
}

const initialState: CreateUpdateLibraryModuleState = {
  loadingCreate: false,
  createdModule: null,

  loadingGet: false,
  getError: null,
  fetchedModule: null,

  loadingUpdate: false,
  updatedModule: null,

  loadingSaveToLibrary: false,
  savedToLibraryModule: null,
};

const createUpdateLibraryModuleSlice = createSlice({
  name: "createUpdateLibraryModule",
  initialState,
  reducers: {
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreatedModule(state, action: PayloadAction<LibraryModule | null>) {
      state.createdModule = action.payload;
    },
    setLoadingGet(state, action: PayloadAction<boolean>) {
      state.loadingGet = action.payload;
    },
    setGetError(state, action: PayloadAction<string | null>) {
      state.getError = action.payload;
    },
    setFetchedModule(state, action: PayloadAction<LibraryModule | null>) {
      state.fetchedModule = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdatedModule(state, action: PayloadAction<LibraryModule | null>) {
      state.updatedModule = action.payload;
    },
    setLoadingSaveToLibrary(state, action: PayloadAction<boolean>) {
      state.loadingSaveToLibrary = action.payload;
    },
    setSavedToLibraryModule(
      state,
      action: PayloadAction<LibraryModule | null>,
    ) {
      state.savedToLibraryModule = action.payload;
    },
    resetCreateUpdateLibraryModuleState(state) {
      state.loadingCreate = false;
      state.createdModule = null;
      state.loadingGet = false;
      state.getError = null;
      state.fetchedModule = null;
      state.loadingUpdate = false;
      state.updatedModule = null;
      state.loadingSaveToLibrary = false;
      state.savedToLibraryModule = null;
    },
  },
});

export const {
  setLoadingCreate,
  setCreatedModule,
  setLoadingGet,
  setGetError,
  setFetchedModule,
  setLoadingUpdate,
  setUpdatedModule,
  setLoadingSaveToLibrary,
  setSavedToLibraryModule,
  resetCreateUpdateLibraryModuleState,
} = createUpdateLibraryModuleSlice.actions;

export default createUpdateLibraryModuleSlice.reducer;

export const createLibraryModuleAction =
  (
    requestData: CreateLibraryModuleRequest,
    onSuccess?: (module: LibraryModule) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    try {
      const response = await createLibraryModule(requestData);
      dispatch(setCreatedModule(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const getLibraryModuleAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingGet(true));
    dispatch(setGetError(null));
    try {
      const response = await getLibraryModule(uuid);
      dispatch(setFetchedModule(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch the library module.";
      dispatch(setGetError(message));
    } finally {
      dispatch(setLoadingGet(false));
    }
  };

export const updateLibraryModuleAction =
  (
    uuid: string,
    requestData: UpdateLibraryModuleRequest,
    onSuccess?: (module: LibraryModule) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    try {
      const response = await updateLibraryModule(uuid, requestData);
      dispatch(setUpdatedModule(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const saveCourseModuleToLibraryAction =
  (
    requestData: CreateLibraryModuleFromCourseModuleRequest,
    onSuccess?: (module: LibraryModule) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingSaveToLibrary(true));
    try {
      const response = await createLibraryModuleFromCourseModule(requestData);
      dispatch(setSavedToLibraryModule(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setLoadingSaveToLibrary(false));
    }
  };
